import React, { useState, useEffect } from 'react';
import SmartShopProducts from './SmartShopProducts';
import SmartShopAssets from './SmartShopAssets';
import BackButton from '../components/BackButton';

// SmartShopHome - the SmartShop sub-tile landing. SmartShop opens here (three tiles)
// instead of straight into the products list. Owns sub-navigation between surfaces.
//   - Product CMS  -> SmartShopProducts (live)
//   - Asset Library -> SmartShopAssets  (live)
//   - Review        -> placeholder, no backend yet (migration/datalake intake, Phase B)
// Glen's design tokens, self-contained. gido flows down to each surface.

// ── DBOS SmartBrand accent legibility (D-0XX) ────────────────────────────────
// Adapt any brand accent so it stays readable against a given background. A too-dark
// accent on a dark bg (or too-light on light) is nudged in HSL lightness - hue and
// saturation preserved, so it remains recognizably the brand colour - until it clears a
// minimum contrast. Bright-enough accents pass through unchanged. Reusable across DBOS
// (SmartShop, SmartMail, SmartWeb, SmartShow, agent image-gen). Standalone by design.
function _hexToRgb(hex) {
  const m = String(hex || '').replace('#', '').match(/.{2}/g);
  if (!m || m.length < 3) return null;
  return m.slice(0, 3).map((x) => parseInt(x, 16));
}
function _rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return [h, s, l];
}
function _hslToHex(h, s, l) {
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue = (t) => {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    r = hue(h + 1 / 3); g = hue(h); b = hue(h - 1 / 3);
  }
  const to = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}
function _luminance(r, g, b) {
  // relative luminance (WCAG-ish), 0..1
  const a = [r, g, b].map((v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}
function _contrast(rgb1, rgb2) {
  const l1 = _luminance(...rgb1) + 0.05;
  const l2 = _luminance(...rgb2) + 0.05;
  return l1 > l2 ? l1 / l2 : l2 / l1;
}
// Returns a hex accent adapted to read against `bg`. Preserves hue/sat, moves lightness.
function legibleAccent(accent, bg) {
  const rgb = _hexToRgb(accent);
  const bgRgb = _hexToRgb(bg) || [10, 12, 10];
  if (!rgb) return accent;
  const TARGET = 4.0; // min contrast ratio for legible text on the bg
  if (_contrast(rgb, bgRgb) >= TARGET) return accent; // already fine (e.g. Glen's red)
  const [h, s, l0] = _rgbToHsl(...rgb);
  const bgIsDark = _luminance(...bgRgb) < 0.2;
  // step lightness toward legibility (lighten on dark bg, darken on light bg)
  let l = l0;
  for (let i = 0; i < 20; i++) {
    l = bgIsDark ? Math.min(1, l + 0.04) : Math.max(0, l - 0.04);
    const cand = _hexToRgb(_hslToHex(h, s, l));
    if (_contrast(cand, bgRgb) >= TARGET) break;
  }
  return _hslToHex(h, s, l);
}

const C = {
  bg: '#0a0c0a', bg2: '#0d0f0d', bg3: '#111311', bg4: '#1a1c1a',
  text: '#F4F1E1', accent: 'var(--ss-accent, #8B1A1A)',
  faint: 'rgba(255,255,255,0.3)', faint2: 'rgba(255,255,255,0.4)',
  line: 'rgba(255,255,255,0.08)', line2: 'rgba(255,255,255,0.06)',
};

const TILES = [
  { id: 'products', title: 'Product CMS', desc: 'Add, edit, and publish products - images, price, stock, variants, options.', live: true },
  { id: 'assets', title: 'Asset Library', desc: 'All product images in one place - assign, reuse, merge, and tidy up.', live: true },
  { id: 'review', title: 'Review', desc: 'Review images and products brought in from migrations or SmartData before they go live.', live: false },
];

const S = {
  page: { background: C.bg, color: C.text, minHeight: '100vh', fontFamily: 'system-ui, sans-serif' },
  header: { background: C.bg, borderBottom: `1px solid ${C.line}`, padding: '0 28px', height: 56, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 },
  h1: { color: C.accent, fontSize: 16, fontStyle: 'italic', fontWeight: 900, fontFamily: 'Georgia, serif' },
  // brand-coloured and large enough to find without hunting - it is the only way back
  back: { background: 'rgba(255,255,255,0.05)', color: C.accent, border: `1px solid ${C.accent}`, padding: '6px 16px', fontWeight: 900, fontSize: 20, lineHeight: 1.1, cursor: 'pointer' },
  wrap: { padding: '48px 28px', maxWidth: 900, margin: '0 auto' },
  // the shop's own name leads; the GIDO is an identifier, not a brand - small, secondary,
  // present on every app surface but never the headline.
  intro: { display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 28 },
  introName: { fontSize: 15, fontWeight: 700, color: C.text, letterSpacing: '0.02em' },
  introGido: { fontSize: 10, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.12em' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 },
  tile: (live) => ({ background: C.bg3, border: `1px solid ${C.line2}`, padding: 24, cursor: live ? 'pointer' : 'default', opacity: live ? 1 : 0.5, position: 'relative', transition: 'border-color 0.2s' }),
  tileTitle: { fontSize: 15, fontWeight: 900, fontFamily: 'Georgia, serif', fontStyle: 'italic', color: C.accent, marginBottom: 8 },
  tileDesc: { fontSize: 12, color: C.faint2, lineHeight: 1.5 },
  soon: { position: 'absolute', top: 16, right: 16, fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.faint, border: `1px solid ${C.line}`, padding: '2px 6px' },
};

function SmartShopHome({ gido, onBack }) {
  const [view, setView] = useState(null); // null (tiles) | 'products' | 'assets'

  // Per-tenant accent (D-062) + auto-legibility (DBOS SmartBrand rule): read the tenant's
  // SmartBrand accent, adapt it so it stays legible on the dark theme (a too-dark accent is
  // lightened in HSL, preserving hue/saturation - still "their colour", just readable), then
  // set --ss-accent. Glen (no visual.colors) -> var unset -> every surface's
  // var(--ss-accent, #8B1A1A) falls back to his authoritative CMS red.
  useEffect(() => {
    let cancelled = false;
    async function loadAccent() {
      if (!gido?.gido_id) return;
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        const snap = await getDoc(doc(db, 'smart_brand', gido.gido_id));
        if (cancelled) return;
        const accent = snap.exists() ? snap.data()?.visual?.colors?.accent : null;
        if (accent) {
          document.documentElement.style.setProperty('--ss-accent', legibleAccent(accent, '#0a0c0a'));
        } else {
          document.documentElement.style.removeProperty('--ss-accent');
        }
      } catch (e) {
        console.error('[smartshop] accent load failed', e);
        document.documentElement.style.removeProperty('--ss-accent');
      }
    }
    loadAccent();
    return () => { cancelled = true; };
  }, [gido]);

  if (view === 'products') return <SmartShopProducts gido={gido} onBack={() => setView(null)} />;
  if (view === 'assets')   return <SmartShopAssets gido={gido} onBack={() => setView(null)} />;

  return (
    <div style={S.page}>
      <header style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <BackButton onClick={onBack} />
          <h1 style={S.h1}>SmartShop</h1>
        </div>
      </header>
      <div style={S.wrap}>
        <div style={S.intro}>
            <span style={S.introName}>{gido?.company_name || gido?.display_name || 'Your shop'}</span>
            <span style={S.introGido}>{gido?.gido_id || ''}</span>
          </div>
        <div style={S.grid}>
          {TILES.map((t) => (
            <div
              key={t.id}
              style={S.tile(t.live)}
              onClick={() => t.live && setView(t.id)}
              onMouseEnter={(e) => { if (t.live) e.currentTarget.style.borderColor = C.accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.line2; }}
            >
              {!t.live && <span style={S.soon}>Coming</span>}
              <div style={S.tileTitle}>{t.title}</div>
              <div style={S.tileDesc}>{t.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default SmartShopHome;